import { FolderDropdown, FolderList, FolderItem, constants } from '@trucktrax/trucktrax-common';
import { PlantDto, RegionDto } from '@trucktrax/trucktrax-ts-common';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import RegionsSelect from '../../shared/sidenav/RegionsSelect';
import {
  ICON_CIRCLE_OUTLINE_CLASS,
  ICON_DOT_CLASS,
  LOGGED_OUT_TITLE,
} from '../../../constants/appConstants';
import { getIdFromUrl } from '../../../util/appUtil';
import { selectTruckMarker } from '../../../services/ticketsService';
import { closeAllPopups, MarkerSelectedFrom } from '../../../store/actions/mapviewActions';
import { ReduxState } from '../../../store';
import incomingActiveMarkersSelector, {
  incomingActivePlantListSelector,
  incomingTicketsForSelectedPlantsSelector,
  selectedIncomingPlantUrlsSelector,
} from '../../../store/selectors/incomingMaterialsSelectors';
import { MapMarker, TicketWithCurrentStatusDto } from '../../../types';
import { getIncomingMaterialsSidebarFolderKey } from '../../../util/statusesUtil';
import styles from './IncomingMaterialsPanelContent.module.scss';

interface StatusStep {
  key: string;
  label: string;
  color: string;
}

// DEV-4157: normalized to 3 folders for both internal and external trucks —
// Ticketed, To Plant, At Plant — mirroring the TT Geo app's status naming.
export const INCOMING_STATUS_WORKFLOW: StatusStep[] = [
  {
    key: constants.STATUS_IDS.TICKETED,
    label: constants.STATUS_NAMES.Ticketed,
    color: constants.STATUS_COLORS.Ticketed,
  },
  {
    key: constants.STATUS_IDS.TO_JOB,
    label: constants.STATUS_NAMES.ToPlant,
    color: constants.STATUS_COLORS.ToJob,
  },
  {
    key: constants.STATUS_IDS.ON_JOB,
    label: constants.STATUS_NAMES.AtPlant,
    color: constants.STATUS_COLORS.OnJob,
  },
];

const STATUS_SORT_ORDER = new Map(INCOMING_STATUS_WORKFLOW.map((s, i) => [s.key, i]));

function getPlantUrls(plants: PlantDto[] = []): string[] {
  return plants.map(p => p.url).filter((url): url is string => !!url);
}

/**
 * Returns the IM workflow folder key for a ticket from `currentStatus.status`.
 * `currentStatus` is the BE's authoritative current workflow state, populated
 * by `AppendStatus` on both the bulk `/tickets/with-current-status` endpoint
 * and on live socket payloads. `externalInfo.status` is intentionally NOT
 * consulted — that field comes from an external feed and frequently reports
 * events outside the IM workflow (e.g. `DeliveryStart`).
 */
function getTicketStatus(ticket: TicketWithCurrentStatusDto): string | undefined {
  const status = ticket.currentStatus?.status;
  return status ? getIncomingMaterialsSidebarFolderKey(String(status)) : undefined;
}

function getStatusSortOrder(status?: string): number {
  if (!status) return 99;
  return STATUS_SORT_ORDER.get(status) ?? 99;
}

function getTruckDisplayName(ticket: TicketWithCurrentStatusDto): string {
  return (
    ticket.truck?.truckAlias ?? ticket.externalInfo?.truckAlias ?? `Ticket #${ticket.ticketNumber}`
  );
}

interface PlantGroup {
  plantUrl: string;
  plantName: string;
  tickets: TicketWithCurrentStatusDto[];
}

export function groupTicketsByPlant(
  tickets: TicketWithCurrentStatusDto[],
  plantList: PlantDto[] = [],
  selectedPlantUrls: Set<string> | undefined = undefined,
): PlantGroup[] {
  const groups = new Map<string, PlantGroup>();
  const plantsByUrl = new Map(plantList.map(p => [p.url, p]));

  tickets
    .filter(ticket => !!ticket.destinationPlant?.url)
    .forEach(ticket => {
      const plantUrl = ticket.destinationPlant!.url!;
      if (!groups.has(plantUrl)) {
        const matchedPlant = plantsByUrl.get(plantUrl);
        groups.set(plantUrl, {
          plantUrl,
          plantName: matchedPlant?.name ?? 'Unknown Plant',
          tickets: [],
        });
      }
      groups.get(plantUrl)!.tickets.push(ticket);
    });

  // All plants in scope should appear, even with zero tickets:
  //  - selectedPlantUrls provided: just the selected URLs (an empty set is a deliberate
  //    deselect-all and yields no groups — DEV-3820). This is what the panel always passes.
  //  - omitted: every plant from plantList, for callers that apply no filter of their own.
  const plantUrls = selectedPlantUrls ?? new Set(getPlantUrls(plantList));
  plantUrls.forEach(url => {
    if (!groups.has(url)) {
      const plant = plantsByUrl.get(url);
      groups.set(url, { plantUrl: url, plantName: plant?.name ?? 'Unknown Plant', tickets: [] });
    }
  });

  Array.from(groups.values()).forEach(group => {
    group.tickets.sort(
      (a, b) => getStatusSortOrder(getTicketStatus(a)) - getStatusSortOrder(getTicketStatus(b)),
    );
  });

  return Array.from(groups.values()).sort((a, b) => a.plantName.localeCompare(b.plantName));
}

const KNOWN_STATUS_KEYS = new Set(INCOMING_STATUS_WORKFLOW.map(s => s.key));

/**
 * A truck with a current ticket but no active map position is treated as
 * "driver logged out": logout is the only flow that deactivates positions
 * (idle/expired-session sweeps are themselves logouts), while the ticket is
 * deliberately left open so the truck stays in the sidebar.
 *
 * DEV-4156: tickets with no truck reference at all (e.g. an external truck
 * whose data only ever populates externalInfo) are treated the same way —
 * they can never match a marker either, so onTruckItemClick is just as dead
 * a click for them, and the sidebar must indicate that rather than render
 * the default filled dot.
 */
export function isTruckLoggedOut(
  ticket: TicketWithCurrentStatusDto,
  markerTruckIds: Set<string>,
): boolean {
  const truckId = getIdFromUrl(ticket.truck?.url);
  return !truckId || !markerTruckIds.has(truckId);
}

// icon/title mirror trucktrax-common's FolderItem per-item overrides
// (DEV-4204, requires >= 1.1.9); typed locally to keep `data` narrower than
// FolderItem's `data?: any`.
interface StatusFolderTruckItem {
  id: string;
  name: string;
  icon?: string;
  title?: string;
  data: { truckUrl?: string; ticketUrl?: string };
}

function buildFolderItem(
  ticket: TicketWithCurrentStatusDto,
  markerTruckIds: Set<string>,
): StatusFolderTruckItem {
  const item: StatusFolderTruckItem = {
    id: ticket.url ?? ticket.ticketNumber?.toString() ?? '',
    name: getTruckDisplayName(ticket),
    data: { truckUrl: ticket.truck?.url, ticketUrl: ticket.url },
  };

  if (isTruckLoggedOut(ticket, markerTruckIds)) {
    item.icon = ICON_CIRCLE_OUTLINE_CLASS;
    item.title = LOGGED_OUT_TITLE;
  }

  return item;
}

function buildStatusFolders(tickets: TicketWithCurrentStatusDto[], markerTruckIds: Set<string>) {
  const ticketsByStatus = new Map<string, TicketWithCurrentStatusDto[]>();
  tickets.forEach(ticket => {
    const status = getTicketStatus(ticket) ?? '';
    if (!ticketsByStatus.has(status)) {
      ticketsByStatus.set(status, []);
    }
    ticketsByStatus.get(status)!.push(ticket);
  });

  const folders = INCOMING_STATUS_WORKFLOW.filter(step => ticketsByStatus.has(step.key)).map(
    step => ({
      id: step.key,
      name: step.label,
      dataTest: `status-${step.key}`,
      color: step.color,
      icon: ICON_DOT_CLASS,
      itemList: ticketsByStatus
        .get(step.key)!
        .map(ticket => buildFolderItem(ticket, markerTruckIds)),
    }),
  );

  // Collect tickets with unexpected statuses into a catch-all folder
  const otherTickets: TicketWithCurrentStatusDto[] = [];
  ticketsByStatus.forEach((items, status) => {
    if (!KNOWN_STATUS_KEYS.has(status)) {
      otherTickets.push(...items);
    }
  });
  if (otherTickets.length > 0) {
    folders.push({
      id: 'other',
      name: 'Other',
      dataTest: 'status-other',
      color: constants.STATUS_COLORS.Unknown,
      icon: ICON_DOT_CLASS,
      itemList: otherTickets.map(ticket => buildFolderItem(ticket, markerTruckIds)),
    });
  }

  return folders;
}

export interface IncomingMaterialsPanelContentProps {
  regions: RegionDto[];
  currentRegionId: string;
  onRegionChange: (regionUrl: string) => void;
  selectedPlantUrls: string[];
  incomingMarkers: MapMarker[];
  selectTruckMarker: (marker: any, ticketUrl: string) => Promise<void>;
  closeAllPopups: () => void;
  incomingPlantList: PlantDto[];
  /** Already scoped to `selectedPlantUrls` (incomingTicketsForSelectedPlantsSelector). */
  incomingTickets: TicketWithCurrentStatusDto[];
}

interface IncomingMaterialsPanelContentState {
  plantsOpen: boolean;
  openPlants: { [plantUrl: string]: boolean };
  openStatusFolders: { [id: string]: boolean };
  selectedItemId: string | null;
}

export class IncomingMaterialsPanelContent extends Component<
  IncomingMaterialsPanelContentProps,
  IncomingMaterialsPanelContentState
> {
  togglePlantHandlers = new Map<string, () => void>();

  constructor(props: IncomingMaterialsPanelContentProps) {
    super(props);
    this.state = {
      plantsOpen: true,
      openPlants: {},
      openStatusFolders: {},
      selectedItemId: null,
    };
  }

  componentDidUpdate(prevProps: IncomingMaterialsPanelContentProps) {
    if (prevProps.currentRegionId !== this.props.currentRegionId) {
      this.setState({
        plantsOpen: true,
        openPlants: {},
        openStatusFolders: {},
        selectedItemId: null,
      });
    }
  }

  togglePlantsDropdown = () => {
    this.setState(prev => ({ plantsOpen: !prev.plantsOpen }));
  };

  getTogglePlantHandler = (plantUrl: string) => {
    if (!this.togglePlantHandlers.has(plantUrl)) {
      this.togglePlantHandlers.set(plantUrl, () => {
        this.setState(prev => ({
          openPlants: { ...prev.openPlants, [plantUrl]: !prev.openPlants[plantUrl] },
        }));
      });
    }
    return this.togglePlantHandlers.get(plantUrl)!;
  };

  onStatusFolderClick = (openStateMap: { [id: string]: boolean }) => {
    this.setState(prev => ({
      openStatusFolders: { ...prev.openStatusFolders, ...openStateMap },
    }));
  };

  onRegionChanged = (region: RegionDto) => {
    this.props.onRegionChange(region.url!);
  };

  onTruckItemClick = (item: FolderItem) => {
    const truckId = getIdFromUrl(item.data?.truckUrl);
    if (!truckId) return;

    const marker = this.props.incomingMarkers.find(m => m.id === truckId);
    if (!marker) return;

    this.setState({ selectedItemId: item.id });
    this.props.closeAllPopups();
    this.props.selectTruckMarker(
      {
        ...marker.data,
        selectedFrom: MarkerSelectedFrom.TruckFolderList,
      },
      item.data?.ticketUrl ?? marker.data.ticketUrl,
    );
  };

  renderPlantFolders = () => {
    const { selectedPlantUrls, incomingTickets, incomingPlantList } = this.props;
    const plantGroups = groupTicketsByPlant(
      incomingTickets,
      incomingPlantList,
      new Set(selectedPlantUrls),
    );

    const markerTruckIds = new Set(this.props.incomingMarkers.map(m => m.id));

    const totalTruckCount = plantGroups.reduce((sum, g) => sum + g.tickets.length, 0);

    return (
      <div className={styles.lowerContent}>
        <FolderDropdown
          dataTestName="plants-dropdown"
          count={totalTruckCount}
          isOpen={this.state.plantsOpen}
          name="Destination Plants"
          onClick={this.togglePlantsDropdown}
          contentClasses={styles.plantsContent}
        >
          {plantGroups.map(group => (
            <div key={group.plantUrl} title={group.plantName} style={{ display: 'contents' }}>
              <FolderDropdown
                dataTestName={`plant-${group.plantName}`}
                icon="icon-home"
                count={group.tickets.length}
                isOpen={!!this.state.openPlants[group.plantUrl]}
                name={group.plantName}
                onClick={this.getTogglePlantHandler(group.plantUrl)}
                classes={styles.plantFolder}
                isActive={false}
              >
                <FolderList
                  folderList={buildStatusFolders(group.tickets, markerTruckIds)}
                  onFolderClick={this.onStatusFolderClick}
                  onItemClick={this.onTruckItemClick}
                  selectedItemId={this.state.selectedItemId}
                  openStateMap={this.state.openStatusFolders}
                  isDarkTheme
                />
              </FolderDropdown>
            </div>
          ))}
        </FolderDropdown>
      </div>
    );
  };

  render() {
    return (
      <div className={styles.panelContent}>
        <RegionsSelect regions={this.props.regions} onSelectionChanged={this.onRegionChanged} />
        {this.renderPlantFolders()}
      </div>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    selectedPlantUrls: selectedIncomingPlantUrlsSelector(state),
    incomingMarkers: incomingActiveMarkersSelector(state),
    incomingPlantList: incomingActivePlantListSelector(state),
    // Derived from the Redux slice so socket-driven updates
    // (SYNC_INCOMING_TICKET / REMOVE_INCOMING_TICKET) flow into the sidebar
    // without requiring a REST refetch. IncomingMaterialsSubscription owns
    // seeding and clearing the slice app-wide.
    incomingTickets: incomingTicketsForSelectedPlantsSelector(state),
  };
}

export default connect(mapStateToProps, {
  selectTruckMarker,
  closeAllPopups,
})(IncomingMaterialsPanelContent);
